import React, { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import ExperienceOfferPageTicketSelection from '../ExperienceOfferPage/ExperienceOfferPageTicketSelection'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import ExperienceOfferPageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import { useExperienceTicketsOptions } from 'hooks/ExperiencePage/useExperienceTicketsOptions'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import { getFormattedDateByTimezone } from 'lib/experiences/experienceUtils'

interface Props {
  experienceView: App.ExperienceBookingView
}

function ExperienceGiveAsGiftModal(props: Props) {
  const {
    experienceView,
  } = props

  const intl = useIntl()
  const pageState = useContext(ExperienceOfferPageStateContext)
  const { redemptionLocation, pickupPoint } = pageState
  const [slots] = useExperienceAvailableTimes(experienceView.originalExperience, {
    isGift: true,
    redemptionLocationId: redemptionLocation?.id,
    pickupPointId: pickupPoint?.id,
  })

  // Format expiration dates with the location timezone
  const bookByDate = experienceView.originalExperience.bookByDate ?? experienceView.originalExperience.expirationDate
  const formattedBookByDate = getFormattedDateByTimezone(bookByDate, experienceView.originalExperience.location.timezone, DMY_CASUAL_FORMAT)
  const formattedExpirationDate = getFormattedDateByTimezone(experienceView.originalExperience.expirationDate, experienceView.originalExperience.location.timezone, DMY_CASUAL_FORMAT)

  const ticketOptions = useExperienceTicketsOptions(slots, { isGift: true })

  const hasExpirationDates = !!(formattedBookByDate || formattedExpirationDate)
  const showExpirationMessage = hasExpirationDates && !ticketOptions.some(ticket => ticket.bookByDate)

  const recipientExpirationMessage = formattedExpirationDate
    ? intl.formatMessage(
        {
          id: 'FNl36cluQmNF5pAjS-NU',
          defaultMessage: 'Your recipient must book by {bookByDate} for a date up to {expirationDate}.*',
          description: 'Message informing gift purchaser of booking deadline and travel date limit for recipient',
        },
        { bookByDate: formattedBookByDate, expirationDate: formattedExpirationDate },
      )
    : intl.formatMessage(
        {
          id: 'aJJyBugtnA_O8OW84vUh',
          defaultMessage: 'Your recipient must book by {bookByDate}.',
          description: 'Message informing gift purchaser of booking deadline for recipient',
        },
        { bookByDate: formattedBookByDate },
      )

  return (
    <Modal
      title={intl.formatMessage({ id: 'pk_AtLsk2QTyoNsdMUVX', defaultMessage: 'Give this experience as a gift', description: 'Modal title displayed when the user chooses to send an experience as a gift' })}
      size="L"
    >
      {experienceView.originalExperience.giftType === 'offer' && <VerticalSpacer gap={8}>
        <BodyText variant="large">
          {intl.formatMessage({
            id: 'n7aiLGieYMAfmm6iVMA3',
            defaultMessage: 'Purchasing this as a gift will secure the offer for your recipient and give them the flexibility to choose their dates at a later time. Dates must be within the book by and validity period and are subject to availability and surcharges.',
            description: 'Explanation of gift offer purchase including date and availability conditions',
          })}
        </BodyText>
        <BodyText variant="large">
          {intl.formatMessage({
            id: 'yKZhb2yKJ7ii17N24qUv',
            defaultMessage: 'Your recipient can manage and make changes to their booking according to our 7-day ‘change of mind’ guarantee after booking.',
            description: 'Information about recipient ability to change booking under change of mind policy',
          })}
        </BodyText>
        {showExpirationMessage && <BodyText variant="large">
          {recipientExpirationMessage}
        </BodyText>}
        <Caption variant="medium">
          {intl.formatMessage({
            id: '1QqGqacj7UULzqK-7jQw',
            defaultMessage: '*Subject to availability and seasonal surcharges.',
            description: 'Disclaimer about availability and surcharges for gift offer',
          })}
        </Caption>
      </VerticalSpacer>}
      {experienceView.originalExperience.giftType === 'gift-card' && <VerticalSpacer gap={8}>
        <BodyText variant="large">
          {intl.formatMessage({
            id: 'OLxf3PfsDHKtZGCPXi2d',
            defaultMessage: 'Purchasing this will send a gift card to your recipient and give them the flexibility to book the experience at a later time.',
            description: 'Explanation of gift card purchase for experiences in the experiences section',
          })}
        </BodyText>
        <BodyText variant="large">
          <FormattedMessage
            id="kellUUF5pKPjEIIG1mdm"
            defaultMessage="Please note Gift Cards are non-refundable once purchased, as per the <link>Terms and Conditions</link>."
            description="Gift card non-refundable notice with link to terms"
            values={{
              link: (chunks) => <TextLink weight="regular" to="/giftcards-terms" target="_blank">{chunks}</TextLink>,
            }}
          />
        </BodyText>
        {showExpirationMessage && <BodyText variant="large">
          {recipientExpirationMessage}
        </BodyText>}
        {!hasExpirationDates && <BodyText variant="large">
          {intl.formatMessage({
            id: 'jAfbRmaJ2JFgPBsHSlK6',
            defaultMessage: 'Your recipient has three years to use their Gift Card*',
            description: 'Information about gift card validity period when no specific expiration dates',
          })}
        </BodyText>}
        <Caption variant="medium">
          {intl.formatMessage({
            id: 'fyI9LzNKsONncZgtp1Pu',
            defaultMessage: '*Subject to availability and seasonal surcharges. Redeem soon to secure this offer or use the voucher for a similar experience of equal value.',
            description: 'Disclaimer about availability and redemption for gift card',
          })}
        </Caption>
      </VerticalSpacer>}
      <ExperienceOfferPageTicketSelection
        isGift
        experienceView={experienceView}
      />
    </Modal>
  )
}

export default ExperienceGiveAsGiftModal
